<template>
  <v-row class="match-height">
    <v-col cols="12">
      <v-card>
        <v-card-title>
          {{ $t("Käyttäjä") }}
          <v-spacer></v-spacer>

        </v-card-title>
        <v-card-text>
          <v-form v-on:submit.prevent="submit" ref="form">
            <v-text-field hide-details="auto" outlined :rules="[required]" :label="$t('Etunimi')"
              v-model="editUser.first_name"></v-text-field>
            <br />
            <v-text-field hide-details="auto" outlined :rules="[required]" :label="$t('Sukunimi')"
              v-model="editUser.last_name"></v-text-field>
            <br />
            <v-text-field hide-details="auto" outlined :rules="phoneRules" type="phone" :label="$t('Puhelin')"
              v-model="editUser.phone" placeholder="Muodossa +358401234567"></v-text-field>
            <br />
            <v-text-field hide-details="auto" outlined type="email" :label="$t('Sähköposti')"
              v-model="editUser.email"></v-text-field>

            <br />
            <v-text-field hide-details="auto" outlined type="text" :label="$t('Työtehtävä')"
              v-model="editUser.title"></v-text-field>
            <br />

            <v-select hide-details="auto" outlined :items="allowedTimezones" :label="$t('Aikavyöhyke')"
              v-model="editUser.timezone" :placeholder="defaultTimezone" clearable></v-select>
            <br />

            <div v-if="userLanguage" class="d-flex mb-4">
              <v-text-field :label="$t('Käyttäjän kielivalinta')" outlined disabled
                :hint="$t('Käyttäjä halitsee tietoa itse')" persistent-hint :value="getLanguageName(userLanguage)">
                <template v-slot:prepend-inner>
                  <div class="d-flex  justify-center mr-1">
                    <img :src="require(`@/assets/images/flags/${userLanguage}.png`)" style="width: 34px;" />
                  </div>
                </template>
              </v-text-field>
            </div>

            <v-switch dense hide-details="auto" v-model="editUser.show_email" :label="$t('Näytä sähköposti kaikille')"
              class="mb-2 mt-0"></v-switch>

            <v-switch dense hide-details="auto" v-model="editUser.show_phone"
              :label="$t('Näytä puhelinnumero kaikille')" class="mb-2 mt-0"></v-switch>

            <br>

            <v-card outlined class="mt-2">
              <v-card-title>{{ $t("Ryhmät") }}</v-card-title>
              <v-card-text>
                <v-alert text small type="info" v-if="editUser.is_tenant_admin">
                  {{
                    $t(
                      "Käyttäjä on yrityksen pääkäyttäjä, jolla on oikeudet julkaista kaikkiin ryhmiin"
                    )
                  }}
                </v-alert>
                <v-container>
                  <template>
                    <v-row v-for="group in user.admin_groups" :key="group.id">
                      <v-col class="ma-0 pa-0">


                        <v-checkbox @change="groupMembershipChanged" class="ma-0 pa-0" :disabled="group.group_type == 1"
                          v-model="memberOfgroups" :label="group.name" :value="group.id"></v-checkbox>
                      </v-col>
                      <v-col class="ma-0 pa-0" v-if="!editUser.is_tenant_admin">
                        <v-checkbox v-if="memberOfgroups.includes(group.id) ||
                          group.group_type == 1
                        " class="ma-0 pa-0" v-model="adminOfGroups" :label="$t('Ryhmän ylläpitäjä')"
                          :value="group.id"></v-checkbox>
                      </v-col>
                    </v-row>
                  </template>
                </v-container>
              </v-card-text>
            </v-card>

            <br />
            <v-card outlined class="mt-0" v-if="activeDepartments.length">
              <v-card-title>{{ $t("Osastot") }}</v-card-title>
              <v-card-text>

                <v-container>
                  <template>
                    <v-row>
                      <v-col class="ma-0 pa-0">
                        <v-checkbox class="ma-0 pa-0" v-model="editUser.departments" :label="dep.name"
                          v-for="dep in tenant.departments" :key="dep.id" :class="{ 'd-none': dep.is_active === false }"
                          :value="dep.id"></v-checkbox>
                      </v-col>
                    </v-row>
                  </template>
                </v-container>
              </v-card-text>
            </v-card>

            <v-switch dense hide-details="auto" v-model="editUser.is_active" :label="$t('Aktiivinen')"
              class="mb-2 mt-3"></v-switch>
            <br />
            <v-btn color="primary" type="submit">{{ $t("Tallenna") }}</v-btn>

            <v-btn v-if="showRemoveUser" color="error" @click="removeUser" class="ml-2" type="button">{{ $t("Poista")
            }}</v-btn>

          </v-form>

        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { i18n } from "@/plugins/i18n";
import useAuth from "@/useAuth";
import { useTranslations } from "@/@core/libs/i18n";
import { required } from "@/@core/utils/validation";
import { useRouter } from "@/@core/utils";
import { supportedLocales } from "@/@core/utils/i18n_utils";
import { allowedTimezones } from "@/@core/utils/timezones";

export default {
  props: ["id"],
  setup(props) {
    const form = ref(null);
    const { t } = useTranslations();
    const { user, tenant } = useAuth();
    const editUser = reactive({
      id: null,
      email: "",
      first_name: "",
      last_name: "",
      title: "",
      phone: "",
      timezone: null,
      is_active: true,
      show_email: false,
      show_phone: false,
      is_tenant_admin: false,
      read_only: false,
      departments: []
    });

    const defaultTimezone = "Europe/Helsinki";

    const userLanguage = ref("");
    const memberOfgroups = ref([]);
    const adminOfGroups = ref([]);
    const { router } = useRouter();
    const showRemoveUser = ref(false);

    const fetchUser = async (id) => {
      const response = await adminApi.getUser(id);
      if (response.ok) {
        editUser.id = response.data.id;
        editUser.email = response.data.email;
        editUser.first_name = response.data.first_name;
        editUser.last_name = response.data.last_name;
        editUser.title = response.data.title;
        editUser.phone = response.data.phone;
        editUser.timezone = response.data.timezone || defaultTimezone;
        editUser.is_active = response.data.is_active;
        editUser.show_email = response.data.show_email;
        editUser.show_phone = response.data.show_phone;
        editUser.is_tenant_admin = response.data.is_tenant_admin;
        editUser.read_only = response.data.read_only;
        editUser.departments = response.data.departments;

        userLanguage.value = response.data.language;

        if (response.data.is_active === false) {
          showRemoveUser.value = true;
        }

        const groupsResponse = await adminApi.getUserGroups(id);
        const adminGroupsResponse = await adminApi.getUserAdminGroups(id);
        if (groupsResponse.ok) {
          memberOfgroups.value = groupsResponse.data.map((g) => g.id);
        }
        if (adminGroupsResponse.ok) {
          adminOfGroups.value = adminGroupsResponse.data.map((g) => g.id);
        }
      } else {
        alert(response.error);
      }
    };

    const submit = async () => {
      if (form.value.validate()) {
        let payload = JSON.parse(JSON.stringify(editUser));

        payload.groups = [];
        memberOfgroups.value.forEach((group) => {
          payload.groups.push({
            id: group,
            isAdmin: adminOfGroups.value.includes(group),
          });
        });

        const response = await adminApi.saveUser(payload);
        if (response.ok) {
          router.push({ name: "admin-user-list" });
        } else {
          alert(response.error);
        }
      }
    };

    const removeUser = async () => {
      if (confirm(i18n.t('Haluatko varmasti poistaa käyttäjän? Toimintoa ei voi perua!'))) {
        const res = await adminApi.removeUser(props.id);
        if (res.ok) {
          router.replace({ name: "admin-user-list" });
        }
        else {
          alert(res.error);
        }
      }
    }

    const groupMembershipChanged = (groups) => {
      adminOfGroups.value.forEach((adminGroup, index) => {
        if (!groups.includes(adminGroup)) {
          adminOfGroups.value.splice(index, 1);
        }
      });
    };

    const phoneRules = computed(() => {
      if (editUser.read_only === true) return [];

      return [required];
    });

    const activeDepartments = computed(() => {
      return tenant.value.departments.filter((a) => a.is_active === true);
    });

    const getLanguageName = (lang) => {
      const locale = supportedLocales.find(a => a.locale == lang);
      if (locale)
        return locale.title;
      return "";
    };

    onMounted(() => {
      if (props.id) {
        fetchUser(props.id);
      } else {
        const mainGroup = user.value.admin_groups.find(
          (g) => g.group_type == 1
        );
        memberOfgroups.value.push(mainGroup.id);
        if (router?.currentRoute?.query?.readonly !== undefined) {
          editUser.read_only = true;
        }
        editUser.timezone = user.value.timezone || defaultTimezone;
      }
    });

    return {
      tenant,
      user,
      editUser,
      memberOfgroups,
      adminOfGroups,
      groupMembershipChanged,
      showRemoveUser,
      removeUser,
      submit,
      form,
      required,
      phoneRules,
      userLanguage,
      activeDepartments,
      getLanguageName,
      allowedTimezones,
      defaultTimezone,
      t,
    };
  },
};
</script>

<style></style>
